import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { getMagazijnLabel } from "@/types/inventory";

export interface StockAlertProduct {
  id: string;
  product_name: string;
  product_sku: string | null;
  product_bc: string | null;
  product_quantity: number | null;
  current_stock: number;
  magazijn: string;
  magazijn_label: string;
  locatie: string;
  eenheid: string;
  moq: number | null;
  last_check: string;
  check_count: number;
}

export const useStockAlertDetails = (notificationId: string | undefined) => {
  return useQuery({
    queryKey: ['stock-alert-details', notificationId],
    queryFn: async () => {
      if (!notificationId) return [];

      // Fetch monitoring records with their check history
      const { data: monitoringData, error: monitoringError } = await supabase
        .from('notification_product_monitoring')
        .select(`
          id,
          product_name,
          product_sku,
          product_bc,
          product_quantity
        `)
        .eq('notification_id', notificationId)
        .eq('is_monitored', true);

      if (monitoringError) throw monitoringError;
      if (!monitoringData || monitoringData.length === 0) return [];

      // Fetch inventory checks for these monitoring records
      const monitoringIds = monitoringData.map(m => m.id);
      const { data: checksData, error: checksError } = await supabase
        .from('notification_inventory_checks')
        .select('*')
        .in('monitoring_id', monitoringIds)
        .eq('was_in_stock', true)
        .order('check_time', { ascending: false });

      if (checksError) throw checksError;

      // Group checks by monitoring_id
      const checksByMonitoring = new Map<string, any[]>();
      checksData?.forEach(check => {
        const existing = checksByMonitoring.get(check.monitoring_id) || [];
        existing.push(check);
        checksByMonitoring.set(check.monitoring_id, existing);
      });

      // Fetch current stock data from voorraad_live2
      const products: StockAlertProduct[] = [];
      
      for (const monitoring of monitoringData) {
        const checks = checksByMonitoring.get(monitoring.id);
        if (!checks || checks.length === 0) continue;

        const latestCheck = checks[0];
        
        // Fetch current voorraad data
        let voorraadData = null;
        if (monitoring.product_sku) {
          const { data } = await supabase
            .from('voorraad_live2')
            .select('*')
            .eq('product_id', monitoring.product_sku)
            .order('voorraad', { ascending: false })
            .limit(1)
            .maybeSingle();
          voorraadData = data;
        } else if (monitoring.product_bc) {
          const { data } = await supabase
            .from('voorraad_live2')
            .select('*')
            .eq('bc', monitoring.product_bc)
            .order('voorraad', { ascending: false })
            .limit(1)
            .maybeSingle();
          voorraadData = data;
        }

        const magazijnNumber = voorraadData?.magazijn ? parseInt(voorraadData.magazijn) : 0;
        
        products.push({
          id: monitoring.id,
          product_name: monitoring.product_name,
          product_sku: monitoring.product_sku,
          product_bc: monitoring.product_bc,
          product_quantity: monitoring.product_quantity,
          current_stock: latestCheck.stock_amount || 0,
          magazijn: voorraadData?.magazijn || '-',
          magazijn_label: magazijnNumber ? getMagazijnLabel(magazijnNumber) : '-',
          locatie: voorraadData?.locatie || '-',
          eenheid: voorraadData?.eenheid || '-',
          moq: voorraadData?.moq || null,
          last_check: latestCheck.check_time,
          check_count: checks.length
        });
      }

      return products;
    },
    enabled: !!notificationId,
    staleTime: 30000, // 30 seconds
  });
};
